import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import Banner from "../components/Banner";
import ConfigurableOverwriteDialog from "../components/ConfigurableOverwriteDialog";
import ConfigurableUploadForm from "../components/ConfigurableUploadForm";
import UploadStatusPanel from "../components/UploadStatusPanel";
import { useError } from "../context/ErrorContext";
import {
  checkConfigurableOverwrite,
  getConfigurableDocumentTypes,
  getConfigurableFormDefinition,
  getConfigurableIngestionStatus,
  resolveConfigurableForm,
  uploadConfigurableDocument,
} from "../utils/configurableUploadApi";
import {
  INGESTION_POLL_INTERVAL_MS,
  INGESTION_POLL_TIMEOUT_MS,
} from "../utils/configurableUploadConstants";
import {
  CheckOverwriteResponse,
  ConfigurableDocumentType,
  ConfigurableFieldDefinition,
  ConfigurableFormValue,
  ConfigurableFormValues,
  ResolveState,
  ResolvedConfigurableField,
  UploadSession,
} from "../types/configurableUpload";

const textResolveFieldTypes = new Set(["year", "free_text"]);
type ConfigurableFieldModel = ConfigurableFieldDefinition | ResolvedConfigurableField;

const createInitialFormValues = (
  fields: ConfigurableFieldDefinition[],
): ConfigurableFormValues => {
  return fields.reduce<ConfigurableFormValues>((values, field) => {
    if (field.fieldType === "multi_select") {
      values[field.key] = Array.isArray(field.defaultValue) ? field.defaultValue : [];
    } else {
      values[field.key] = field.defaultValue ?? null;
    }
    return values;
  }, {});
};

const toTransportFormValues = (formValues: ConfigurableFormValues) => {
  return Object.entries(formValues).reduce<Record<string, string | string[] | null>>(
    (values, [key, value]) => {
      if (value instanceof File) {
        return values;
      }
      values[key] = value;
      return values;
    },
    {},
  );
};

const mergeResolvedFields = (
  existingFields: ConfigurableFieldModel[],
  resolvedFields: ResolvedConfigurableField[],
) => {
  const existingByKey = new Map(existingFields.map((field) => [field.key, field]));

  return resolvedFields.map((resolvedField) => {
    const existingField = existingByKey.get(resolvedField.key);
    const resolvedOptions = resolvedField.options;
    const hasResolvedOptions = Array.isArray(resolvedOptions) && resolvedOptions.length > 0;
    return {
      ...existingField,
      ...resolvedField,
      order: resolvedField.order ?? existingField?.order ?? 0,
      label: resolvedField.label ?? existingField?.label ?? resolvedField.key,
      editable: resolvedField.editable ?? existingField?.editable ?? resolvedField.enabled,
      placeholder: resolvedField.placeholder ?? existingField?.placeholder ?? null,
      helpText: resolvedField.helpText ?? existingField?.helpText ?? null,
      accept: resolvedField.accept ?? existingField?.accept ?? null,
      options: hasResolvedOptions
        ? resolvedOptions
        : existingField?.options ?? resolvedOptions ?? [],
    };
  });
};

const getImpactedResolveFieldKeys = (
  fields: ConfigurableFieldModel[],
  triggeringFieldKey?: string,
) => {
  if (!triggeringFieldKey) {
    return fields
      .filter((field) => field.visible && field.fieldType !== "hidden")
      .map((field) => field.key);
  }

  const triggeringField = fields.find((field) => field.key === triggeringFieldKey);
  if (!triggeringField) {
    return [triggeringFieldKey];
  }

  const triggeringDependencyRuleKey = triggeringField.dependencyRuleKey;
  const impactedFieldKeys = new Set<string>([triggeringField.key]);

  fields.forEach((field) => {
    if (field.key === triggeringField.key || !field.visible || field.fieldType === "hidden") {
      return;
    }

    if (
      triggeringDependencyRuleKey &&
      field.dependencyRuleKey === triggeringDependencyRuleKey
    ) {
      impactedFieldKeys.add(field.key);
    }
  });

  return Array.from(impactedFieldKeys);
};

const getSelectedFile = (formValues: ConfigurableFormValues) => {
  return Object.values(formValues).find((value): value is File => value instanceof File) ?? null;
};

const getErrorStatus = (error: unknown) => {
  const maybeError = error as { status?: number; response?: { status?: number } };
  return maybeError.status ?? maybeError.response?.status;
};

const UploadPage: React.FC = () => {
  const { showError } = useError();
  const [documentTypes, setDocumentTypes] = useState<ConfigurableDocumentType[]>([]);
  const [selectedDocumentTypeKey, setSelectedDocumentTypeKey] = useState("");
  const [fields, setFields] = useState<Array<ConfigurableFieldDefinition | ResolvedConfigurableField>>([]);
  const [formValues, setFormValues] = useState<ConfigurableFormValues>({});
  const [formLevelMessages, setFormLevelMessages] = useState<string[]>([]);
  const [pageMessage, setPageMessage] = useState("");
  const [isUploadTypeUnavailable, setIsUploadTypeUnavailable] = useState(false);
  const [isLoadingDocumentTypes, setIsLoadingDocumentTypes] = useState(true);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [resolveState, setResolveState] = useState<ResolveState>({
    isResolving: false,
    pendingFieldKeys: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overwriteResponse, setOverwriteResponse] = useState<CheckOverwriteResponse | null>(null);
  const [uploadSessions, setUploadSessions] = useState<UploadSession[]>([]);

  const resolveSequenceRef = useRef(0);
  const formLoadSequenceRef = useRef(0);
  const textResolveTimeoutRef = useRef<number | null>(null);
  const latestFormValuesRef = useRef<ConfigurableFormValues>({});
  const latestFieldsRef = useRef<ConfigurableFieldModel[]>([]);
  const pollingTimersRef = useRef<Map<string, number>>(new Map());

  const selectedDocumentType = useMemo(
    () => documentTypes.find((type) => type.key === selectedDocumentTypeKey) ?? null,
    [documentTypes, selectedDocumentTypeKey],
  );

  const updateUploadSession = useCallback(
    (sessionId: string, patch: Partial<UploadSession>) => {
      setUploadSessions((sessions) =>
        sessions.map((session) =>
          session.id === sessionId ? { ...session, ...patch } : session,
        ),
      );
    },
    [],
  );

  const stopPolling = useCallback((sessionId: string) => {
    const timer = pollingTimersRef.current.get(sessionId);
    if (timer) {
      window.clearTimeout(timer);
      pollingTimersRef.current.delete(sessionId);
    }
  }, []);

  const schedulePolling = useCallback(
    (sessionId: string, uploadId: string, startedAt: number) => {
      const poll = async () => {
        try {
          const response = await getConfigurableIngestionStatus(uploadId);
          if (response.ingestedStatus) {
            updateUploadSession(sessionId, {
              message: response.message,
              state: "success",
            });
            stopPolling(sessionId);
            return;
          }

          if (Date.now() - startedAt >= INGESTION_POLL_TIMEOUT_MS) {
            updateUploadSession(sessionId, {
              message: response.message || "Ingestion tracking timed out.",
              state: "timeout",
            });
            stopPolling(sessionId);
            return;
          }

          updateUploadSession(sessionId, {
            message: response.message,
            state: "pending",
          });
          const nextTimer = window.setTimeout(poll, INGESTION_POLL_INTERVAL_MS);
          pollingTimersRef.current.set(sessionId, nextTimer);
        } catch (error) {
          console.error("Failed to poll ingestion status:", error);
          const status = getErrorStatus(error);
          updateUploadSession(sessionId, {
            message:
              status === 400
                ? "Unable to track ingestion because the tracking token is invalid."
                : status === 404
                  ? "Unable to track ingestion because the upload was not found or access was denied."
                  : "Unable to track ingestion status for this upload.",
            state: "error",
          });
          stopPolling(sessionId);
        }
      };

      const timer = window.setTimeout(poll, INGESTION_POLL_INTERVAL_MS);
      pollingTimersRef.current.set(sessionId, timer);
    },
    [stopPolling, updateUploadSession],
  );

  useEffect(() => {
    const loadDocumentTypes = async () => {
      setIsLoadingDocumentTypes(true);
      setPageMessage("");
      try {
        const response = await getConfigurableDocumentTypes();
        setDocumentTypes(response.documentTypes);
        if (response.documentTypes.length === 0) {
          setPageMessage("No upload types are currently available");
        }
      } catch (error) {
        console.error("Failed to load configurable document types:", error);
        setPageMessage("Unable to load upload configuration. Please try again later.");
        showError("Unable to load upload configuration. Please try again later.");
      } finally {
        setIsLoadingDocumentTypes(false);
      }
    };

    loadDocumentTypes();
  }, [showError]);

  useEffect(() => {
    return () => {
      if (textResolveTimeoutRef.current) {
        window.clearTimeout(textResolveTimeoutRef.current);
      }
      pollingTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      pollingTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    latestFormValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    latestFieldsRef.current = fields;
  }, [fields]);

  const applyResolveResponse = useCallback(
    (
      responseFields: ResolvedConfigurableField[],
      messages: string[],
      currentFields: ConfigurableFieldModel[],
    ) => {
      const mergedFields = mergeResolvedFields(currentFields, responseFields);
      setFields(mergedFields);
      setFormLevelMessages(messages);
      setFormValues((currentValues) => {
        const nextValues = { ...currentValues };
        responseFields.forEach((field) => {
          if (currentValues[field.key] instanceof File && field.fieldType === "file") {
            return;
          }
          nextValues[field.key] = field.value;
        });
        return nextValues;
      });
    },
    [],
  );

  const runResolve = useCallback(
    async (
      values: ConfigurableFormValues,
      currentFields: ConfigurableFieldModel[],
      documentTypeKey = selectedDocumentTypeKey,
      triggeringFieldKey?: string,
    ) => {
      if (!documentTypeKey) return;

      const sequence = resolveSequenceRef.current + 1;
      resolveSequenceRef.current = sequence;
      setResolveState({
        isResolving: true,
        pendingFieldKeys: getImpactedResolveFieldKeys(currentFields, triggeringFieldKey),
      });
      try {
        const response = await resolveConfigurableForm({
          documentTypeKey,
          formValues: toTransportFormValues(values),
        });

        if (sequence !== resolveSequenceRef.current) {
          return;
        }

        applyResolveResponse(response.fields, response.formLevelMessages, currentFields);
      } catch (error) {
        console.error("Failed to resolve configurable form:", error);
        if (getErrorStatus(error) === 404) {
          setIsUploadTypeUnavailable(true);
          setPageMessage("Upload type unavailable");
          showError("Upload type unavailable");
        } else {
          showError("Unable to update form options. Please try again.");
        }
      } finally {
        if (sequence === resolveSequenceRef.current) {
          setResolveState({ isResolving: false, pendingFieldKeys: [] });
        }
      }
    },
    [applyResolveResponse, selectedDocumentTypeKey, showError],
  );

  const handleDocumentTypeChange = async (documentTypeKey: string) => {
    resolveSequenceRef.current += 1;
    if (textResolveTimeoutRef.current) {
      window.clearTimeout(textResolveTimeoutRef.current);
      textResolveTimeoutRef.current = null;
    }
    setSelectedDocumentTypeKey(documentTypeKey);
    setFields([]);
    setFormValues({});
    setFormLevelMessages([]);
    setOverwriteResponse(null);
    setResolveState({ isResolving: false, pendingFieldKeys: [] });
    setPageMessage("");
    setIsUploadTypeUnavailable(false);

    if (!documentTypeKey) {
      return;
    }

    setIsLoadingForm(true);
    const formLoadSequence = formLoadSequenceRef.current + 1;
    formLoadSequenceRef.current = formLoadSequence;
    try {
      const response = await getConfigurableFormDefinition(documentTypeKey);
      if (formLoadSequence !== formLoadSequenceRef.current) {
        return;
      }

      const sortedFields = [...response.fields].sort((a, b) => a.order - b.order);
      const initialFormValues = createInitialFormValues(sortedFields);
      setFields(sortedFields);
      setFormValues(initialFormValues);

      if (response.initialResolutionRequired) {
        await runResolve(initialFormValues, sortedFields, documentTypeKey);
      }
    } catch (error) {
      if (formLoadSequence !== formLoadSequenceRef.current) {
        return;
      }

      console.error("Failed to load configurable form:", error);
      if (getErrorStatus(error) === 404) {
        setIsUploadTypeUnavailable(true);
        setPageMessage("Upload type unavailable");
        showError("Upload type unavailable");
      } else {
        setPageMessage("Unable to load upload metadata. Please try again later.");
        showError("Unable to load upload metadata. Please try again later.");
      }
    } finally {
      if (formLoadSequence === formLoadSequenceRef.current) {
        setIsLoadingForm(false);
      }
    }
  };

  const handleFieldChange = (
    field: ConfigurableFieldDefinition | ResolvedConfigurableField,
    value: ConfigurableFormValue,
  ) => {
    const nextValues = { ...latestFormValuesRef.current, [field.key]: value };
    latestFormValuesRef.current = nextValues;
    setFormValues(nextValues);

    if (field.fieldType === "file" || !field.dependencyRuleKey) {
      return;
    }

    if (textResolveTimeoutRef.current) {
      window.clearTimeout(textResolveTimeoutRef.current);
      textResolveTimeoutRef.current = null;
    }

    if (textResolveFieldTypes.has(field.fieldType)) {
      textResolveTimeoutRef.current = window.setTimeout(() => {
        textResolveTimeoutRef.current = null;
        runResolve(
          latestFormValuesRef.current,
          latestFieldsRef.current,
          selectedDocumentTypeKey,
          field.key,
        );
      }, 500);
      return;
    }

    runResolve(nextValues, latestFieldsRef.current, selectedDocumentTypeKey, field.key);
  };

  const addUploadSession = useCallback(
    (fileName: string, message: string, uploadId?: string | null, trackIngestion = false) => {
      const sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const startedAt = Date.now();
      const session: UploadSession = {
        id: sessionId,
        uploadId,
        fileName,
        message,
        state: trackIngestion ? "pending" : "success",
        startedAt,
      };
      setUploadSessions((sessions) => [session, ...sessions]);

      if (trackIngestion && uploadId) {
        schedulePolling(sessionId, uploadId, startedAt);
      }
    },
    [schedulePolling],
  );

  const performUpload = async () => {
    if (!selectedDocumentTypeKey) return;

    const documentTypeKey = selectedDocumentTypeKey;
    const selectedFile = getSelectedFile(formValues);
    if (!selectedFile) {
      showError("Please choose a file before uploading.");
      return;
    }

    setIsSubmitting(true);
    try {
      const uploadResponse = await uploadConfigurableDocument(
        selectedDocumentTypeKey,
        toTransportFormValues(formValues),
        selectedFile,
      );

      addUploadSession(
        uploadResponse.fileName,
        uploadResponse.message,
        uploadResponse.uploadId,
        uploadResponse.ingestionTrackingEnabled,
      );
      await handleDocumentTypeChange(documentTypeKey);
    } catch (error) {
      console.error("Failed to upload configurable document:", error);
      if (getErrorStatus(error) === 404) {
        setIsUploadTypeUnavailable(true);
        setPageMessage("Upload type unavailable");
        showError("Upload type unavailable");
      } else {
        showError("File upload failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDocumentTypeKey) return;

    const selectedFile = getSelectedFile(formValues);
    if (!selectedFile) {
      showError("Please choose a file before uploading.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await checkConfigurableOverwrite({
        documentTypeKey: selectedDocumentTypeKey,
        formValues: toTransportFormValues(formValues),
        fileName: selectedFile.name,
      });

      if (!response.canProceed) {
        showError("This upload cannot proceed with the selected values.");
        return;
      }

      if (response.requiresConfirmation) {
        setOverwriteResponse(response);
        return;
      }

      await performUpload();
    } catch (error) {
      console.error("Failed to check overwrite state:", error);
      if (getErrorStatus(error) === 404) {
        setIsUploadTypeUnavailable(true);
        setPageMessage("Upload type unavailable");
        showError("Upload type unavailable");
      } else {
        showError("Unable to check for existing files. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const dismissUploadSession = (sessionId: string) => {
    stopPolling(sessionId);
    setUploadSessions((sessions) =>
      sessions.filter((session) => session.id !== sessionId),
    );
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: "grey.100" }}>
      <Banner current="upload" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <UploadStatusPanel sessions={uploadSessions} onDismiss={dismissUploadSession} />
        <Card sx={{ maxWidth: 720, mx: "auto", mt: 3 }}>
          <CardContent>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                  Upload Document
                </Typography>
                <Typography color="text.secondary">
                  Select an upload type and complete the fields returned by the backend configuration.
                </Typography>
              </Box>

              {isLoadingDocumentTypes ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {pageMessage && <Alert severity="warning">{pageMessage}</Alert>}
                  <FormControl fullWidth disabled={documentTypes.length === 0}>
                    <InputLabel id="document-type-label">Upload type</InputLabel>
                    <Select
                      labelId="document-type-label"
                      value={selectedDocumentTypeKey}
                      label="Upload type"
                      onChange={(event) => handleDocumentTypeChange(event.target.value)}
                      renderValue={(value) =>
                        documentTypes.find((type) => type.key === value)?.displayName ?? ""
                      }
                    >
                      {documentTypes.map((type) => (
                        <MenuItem key={type.key} value={type.key} disabled={!type.enabled}>
                          <Box>
                            <Typography>{type.displayName}</Typography>
                            {type.description && (
                              <Typography variant="caption" color="text.secondary">
                                {type.description}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedDocumentType && isLoadingForm && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {selectedDocumentType && !isLoadingForm && fields.length > 0 && (
                    <ConfigurableUploadForm
                      fields={fields}
                      formValues={formValues}
                      formLevelMessages={formLevelMessages}
                      isSubmitting={isSubmitting}
                      isResolving={resolveState.isResolving}
                      pendingFieldKeys={resolveState.pendingFieldKeys}
                      disabled={!selectedDocumentType.enabled || isUploadTypeUnavailable}
                      onFieldChange={handleFieldChange}
                      onSubmit={handleSubmit}
                    />
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
      <ConfigurableOverwriteDialog
        open={!!overwriteResponse}
        confirmationMessage={overwriteResponse?.confirmationMessage}
        existingFiles={overwriteResponse?.existingFiles ?? []}
        isSubmitting={isSubmitting}
        onCancel={() => setOverwriteResponse(null)}
        onConfirm={performUpload}
      />
    </Box>
  );
};

export default UploadPage;
